import _ from "underscore";
import rison from "rison";
import DriveView from "../../DriveView";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import { TrailerAttachments } from "../../model/TrailerAttachments";
import Sync from "../../sync/sync";
import { generateChecklist } from "../framework/checklist";
import { TrailerAttachment, ITrailerAttachment } from "../../model/TrailerAttachment";
import { Dialog } from "../framework/dialog";
import { isDriverOnWorkflow } from "../../utils/uri";
import { htmlEscape } from "../../utils/vanilla";
import { AuditType, auditSkip } from "../../utils/auditHelper";
import { CancelablePromise as Promise } from "../../utils/cancelablePromise";
import { getAttachmentsFromState, setAttachmentsInState } from "./helper";
import { handleApi } from "../../utils/error";

import pageHTML from "./manageTrailers.tmpl";
import TRAILER_TEMPLATE from "./manageTrailersListItem.tmpl";
import dialogHtml from "./trailerInspectionDialog.tmpl";
import { DriveStore } from "../../store/reducers";
import PerformanceProfilerFactory from "../../utils/performanceProfiler/performanceProfilerFactory";
import { MeasureName } from "../../utils/performanceProfiler/measureName";

interface ITrailerAttachmentViewLog extends ITrailerAttachment {
    removeLabel: string;
}

export default class ManageTrailersView extends DriveView<Backbone.Model> {

    protected strings = [
        "Remove {0} trailer"
    ];
    private readonly trailerAttachments: TrailerAttachments;
    private readonly store: DriveStore;
    private readonly sync: Sync;
    private readonly workflow: boolean;
    private readonly template = _.template(pageHTML);
    private listView;

    constructor (options) {
        super(options);
        this.store = this.app.store;
        this.sync = this.app.sync;
        this.workflow = options.workflow || false;
        this.trailerAttachments = new TrailerAttachments(this.app.api, this.app.store);
        this.fetchTrailerAttachments();
        this.listenTo(dispatcher, DispatcherEvents.SyncTrailerAttachments, () => {
            this.refresh();
            this.trigger("updateChecklist");
        });
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click .removeTrailer": this.removeTrailer
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    refresh(render = true) {
        this.fetchTrailerAttachments();
        render && this.render();
    }

    render () {
        let hasTrailers = !!this.trailerAttachments.length;
        Promise.all([
            this.display(this.template({ hasTrailers })),
            this.translate()
        ])
        .then(() => {
            this.listView && this.listView.remove();
            let listContainer = document.getElementById("trailers-list");
            if (hasTrailers && listContainer) {
                this.listView = generateChecklist({
                    items: (() => {
                        let results: ITrailerAttachmentViewLog[] = [];
                        for (let attachment of this.trailerAttachments.models) {
                            results.push(this.getTrailerViewLog(attachment));
                        }
                        return results;
                    })(),
                    labelTemplate: TRAILER_TEMPLATE,
                    isCheckable: false,
                    context: this,
                    translator: this.translator,
                    el: listContainer
                });
            }
        })
        .catch(handleApi);
        return this;
    }

    getCurrentTrailerAttachments () {
        return this.trailerAttachments.models;
    }

    remove () {
        this.listView && this.listView.remove();
        super.remove();
        return this;
    }

    private fetchTrailerAttachments () {
        this.trailerAttachments.fetch();
        this.trailerAttachments.add(getAttachmentsFromState(this.app.store, this.app.userService));
    }

    private syncOrRemoveAttachment(attachment: TrailerAttachment) {
        let removeTrailerProfiler = PerformanceProfilerFactory.getInstance(MeasureName.RemoveTrailer);
        if (!attachment._isTemp) {
            return removeTrailerProfiler.measureAsync(() => this.sync.trailerAttachments.setItem(attachment, this.workflow));
        }
        removeTrailerProfiler.start();
        let tempAttachments = getAttachmentsFromState(this.app.store, this.app.userService);
        let removedId = attachment.id;
        tempAttachments = tempAttachments.filter(tempAttachment => tempAttachment.id !== removedId);
        setAttachmentsInState(this.app.store, tempAttachments, this.app.userService);
        removeTrailerProfiler.end();
        return Promise.resolve();
    }

    private removeTrailer (e) {
        let id = e.currentTarget.getAttribute("data-attachment");
        let trailerAttachment = (this.trailerAttachments.get(id));
        let trailer = trailerAttachment.getAttachmentTrailer(this.store);

        let detachTrailer = (trailerAtt: TrailerAttachment) => {
            trailerAtt.detach();
            this.trailerAttachments.remove(id);
            this.render();
            this.trigger("updateChecklist");
            return this.syncOrRemoveAttachment(trailerAtt).then(() => {
                this.refresh(false);
                this.trigger("trailerRemoved", trailer.id);
            });
        };
        if (isDriverOnWorkflow() || !this.app.securityService.can("useDvir") || trailer._isTemp) {
            void detachTrailer(trailerAttachment);
        } else {
            let message = _.template(dialogHtml);
            let trailerInspectionDialog = new Dialog(
                "trailer-inspection",
                message({ trailerName: htmlEscape(trailer.name) }),
                this.translator,
                "Inspection",
                [
                    {
                        text: "Skip",
                        className: "button_secondary",
                        id: "skip-inspection",
                        callback: () => {
                            trailerInspectionDialog.waiting.start();

                            let optionalParams = {
                                assetType: "trailer",
                                assetId: trailer.id,
                                assetName: trailer.name
                            };

                            auditSkip(this.app.api, AuditType.DvirInspect, "Detach trailer", optionalParams);
                            void detachTrailer(trailerAttachment).then(() => trailerInspectionDialog.remove());
                        }
                    },
                    {
                        text: "Inspect",
                        id: "start-trailer-inspection",
                        callback: () => {
                            trailerInspectionDialog.waiting.start();

                            let trailerId = trailerAttachment.trailer?.id;
                            let dvirLog = this.app.dvirLogsService.getLatestLog(this.app.dvirLogsService.getDvirLogs(), trailerId);
                            let dvirState: any = {
                                detach: true,
                                trailerAttachment: id
                            };

                            trailerInspectionDialog.remove();
                            if (dvirLog) {
                                dvirState.id = dvirLog.id;
                            } else {
                                dvirState.assetId = trailerId;
                            }
                            this.app.router.navigate(`dvir/inspect,${ rison.encode(dvirState) }`, true);
                        }
                    }
                ],
                {
                    isNightMode: this.app.store.getState().data.nightMode
                }
            );
            void trailerInspectionDialog.open();
        }
    }

    private readonly getTrailerViewLog = (trailerAttachment: TrailerAttachment): ITrailerAttachmentViewLog => {
        let trailerAttachmentObj = trailerAttachment.toJSON();
        const attachedTrailer = trailerAttachment.getAttachmentTrailer(this.store);
        trailerAttachmentObj.trailer = attachedTrailer;
        return { ...trailerAttachmentObj, removeLabel: this.getString("Remove {0} trailer").replace("{0}", attachedTrailer.name ?? "") };
    };
}